import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../context/AuthContext';
import { useFamily } from '../../context/FamilyContext';
import { createExpense, updateExpenseWithAudit, uploadReceipt, deleteReceipt, getExpenseReceipts } from '@coparent/shared/src/services/expenseService';
import { EXPENSE_CATEGORIES } from '@coparent/shared/src/utils/constants';
import ReceiptUpload from './ReceiptUpload';
import ChildMultiSelect from '../common/ChildMultiSelect';
import { HiX } from 'react-icons/hi';
import toast from 'react-hot-toast';
import { toJsDate } from '@coparent/shared/src/utils/dateUtils';
import { format } from 'date-fns';

const ExpenseForm = ({ onClose, expense }) => {
  const { currentUser } = useAuth();
  const { familyId, familyChildren, childrenMap, family, getAllParticipants } = useFamily();
  const [loading, setLoading] = useState(false);
  const [selectedChildIds, setSelectedChildIds] = useState(expense?.childIds || []);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [removedReceipts, setRemovedReceipts] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const isEditing = !!expense;
  const existingReceipts = isEditing
    ? getExpenseReceipts(expense).filter(r => !removedReceipts.some(rm => rm.storagePath === r.storagePath))
    : [];

  const getDateString = (date) => {
    if (!date) return new Date().toISOString().split('T')[0];
    const d = toJsDate(date);
    return format(d, 'yyyy-MM-dd');
  };

  const { register, handleSubmit, watch, formState: { errors } } = useForm({
    defaultValues: isEditing ? {
      description: expense.description || '',
      amount: expense.amount || '',
      date: getDateString(expense.date),
      category: expense.category || 'other',
      splitRatio: expense.splitRatio ?? 50,
      paidBy: expense.paidBy || currentUser.uid,
      note: expense.note || '',
    } : {
      splitRatio: family?.settings?.defaultSplitRatio || 50,
      date: new Date().toISOString().split('T')[0],
      category: 'other',
      paidBy: currentUser.uid,
    }
  });

  const handleRemoveExisting = (receiptObj) => {
    setRemovedReceipts(prev => [...prev, receiptObj]);
  };

  const handleRemovePending = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddFile = (file) => {
    setPendingFiles(prev => [...prev, file]);
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      if (isEditing) {
        const updates = {
          description: data.description,
          amount: parseFloat(data.amount),
          date: new Date(data.date + 'T00:00:00'),
          category: data.category,
          childIds: selectedChildIds,
          paidBy: data.paidBy,
          splitRatio: parseInt(data.splitRatio),
          owedAmount: parseFloat(data.amount) * (parseInt(data.splitRatio) / 100),
          note: data.note || null,
        };
        await updateExpenseWithAudit(familyId, expense.id, updates, expense, currentUser);

        if (removedReceipts.length || pendingFiles.length) {
          setIsUploadingReceipt(true);
          try {
            for (const receiptObj of removedReceipts) {
              await deleteReceipt(familyId, expense.id, receiptObj);
            }
            for (const file of pendingFiles) {
              await uploadReceipt(familyId, expense.id, file, setUploadProgress);
            }
          } catch {
            toast.error('Expense saved but some receipt changes failed');
          }
          setIsUploadingReceipt(false);
        }

        toast.success('Expense updated!');
      } else {
        const result = await createExpense(familyId, {
          description: data.description,
          amount: parseFloat(data.amount),
          date: new Date(data.date + 'T00:00:00'),
          category: data.category,
          childIds: selectedChildIds,
          paidBy: data.paidBy || currentUser.uid,
          splitRatio: parseInt(data.splitRatio),
          note: data.note || null,
          createdBy: currentUser.uid
        });

        if (pendingFiles.length) {
          setIsUploadingReceipt(true);
          try {
            for (const file of pendingFiles) {
              await uploadReceipt(familyId, result.id, file, setUploadProgress);
            }
          } catch {
            toast.error('Expense added but receipt upload failed');
          }
          setIsUploadingReceipt(false);
        }

        toast.success('Expense added!');
      }
      onClose();
    } catch (error) {
      toast.error(isEditing ? 'Failed to update expense' : 'Failed to add expense');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-lg font-bold text-ink-900">{isEditing ? 'Edit Expense' : 'Add Expense'}</h3>
          <button onClick={onClose} className="w-8 h-8 rounded-lg flex items-center justify-center text-ink-400 hover:bg-surface-100 hover:text-ink-600 transition-colors">
            <HiX className="text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Description</label>
            <input
              {...register('description', { required: 'Required' })}
              type="text"
              className="input-base"
              placeholder="What was this expense for?"
            />
            {errors.description && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.description.message}</p>}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Amount</label>
              <div className="relative">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300 font-semibold">$</span>
                <input
                  {...register('amount', { required: 'Required', min: { value: 0.01, message: 'Must be > 0' } })}
                  type="number"
                  step="0.01"
                  className="input-base pl-8"
                  placeholder="0.00"
                />
              </div>
              {errors.amount && <p className="mt-1.5 text-xs text-accent-600 font-medium">{errors.amount.message}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Date</label>
              <input
                {...register('date', { required: 'Required' })}
                type="date"
                className="input-base"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Category</label>
            <select
              {...register('category')}
              className="input-base"
            >
              {EXPENSE_CATEGORIES.map(cat => (
                <option key={cat.value} value={cat.value}>{cat.label}</option>
              ))}
            </select>
          </div>

          {familyChildren.length > 0 && (
            <ChildMultiSelect
              familyChildren={familyChildren}
              childrenMap={childrenMap}
              selectedChildIds={selectedChildIds}
              onChange={setSelectedChildIds}
            />
          )}

          {family && (
            <div>
              <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Paid by</label>
              <select
                {...register('paidBy')}
                className="input-base"
              >
                {getAllParticipants().map(p => (
                  <option key={p.id} value={p.id}>
                    {p.displayName}{p.role === 'household_member' ? ' (household)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">
              Split: other parent pays
            </label>
            <div className="flex items-center gap-3">
              <input
                {...register('splitRatio')}
                type="range"
                min="0"
                max="100"
                className="flex-1 accent-ink-800"
              />
              <span className="text-sm font-bold text-ink-700 w-12 text-right">
                {watch('splitRatio')}%
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-600 mb-1.5 uppercase tracking-wide">Note (optional)</label>
            <input
              {...register('note')}
              type="text"
              className="input-base"
              placeholder="Additional details..."
            />
          </div>

          <ReceiptUpload
            existingReceipts={existingReceipts}
            pendingFiles={pendingFiles}
            onFileSelected={handleAddFile}
            onRemovePending={handleRemovePending}
            onRemoveExisting={handleRemoveExisting}
            uploading={isUploadingReceipt}
            uploadProgress={uploadProgress}
            disabled={loading}
          />

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || isUploadingReceipt}
              className="btn-primary flex-1"
            >
              {isUploadingReceipt ? 'Uploading receipt...' : loading ? (isEditing ? 'Saving...' : 'Adding...') : (isEditing ? 'Save Changes' : 'Add Expense')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExpenseForm;
